/**
 * Centralized width calculation utility
 * Standardizes width calculations across all modules based on sidebar state
 */

// Constants
const SIDEBAR_WIDTH_EXPANDED = 240;
const SIDEBAR_WIDTH_COLLAPSED = 80;
const CONTENT_MARGIN = 32 * 2; // var(--spacing-xl) * 2 = 64px total
const CONTENT_PADDING = 48 * 2; // var(--spacing-2xl) * 2 = 96px total
const TOTAL_FIXED_SPACE = CONTENT_MARGIN + CONTENT_PADDING; // 160px

/**
 * Calculate available width for content based on sidebar state
 * @param {boolean} sidebarCollapsed - Whether the sidebar is collapsed
 * @param {number} buffer - Additional buffer space (default: 40px for scrollbars, etc.)
 * @returns {number|string} Available width in pixels for desktop, or '100%' for mobile/tablet
 */
export const calculateAvailableWidth = (sidebarCollapsed, buffer = 40) => {
  if (typeof window === 'undefined') {
    return 0;
  }

  const width = window.innerWidth;
  
  // Mobile and Tablet: use 100% width for responsive behavior
  if (width < 1024) {
    return '100%';
  }

  // Desktop: calculate based on sidebar
  const sidebarWidth = sidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;
  const availableWidth = width - sidebarWidth - TOTAL_FIXED_SPACE - buffer;
  
  return Math.max(availableWidth, 0); // Ensure non-negative
};

/**
 * Calculate table scroll width based on sidebar state and column constraints
 * @param {boolean} sidebarCollapsed - Whether the sidebar is collapsed
 * @param {number} minWidth - Minimum scroll width (default: 600px) - should match column sum
 * @param {number} maxWidth - Maximum scroll width (sum of all column widths) - should match column sum
 * @param {number} buffer - Additional buffer space (default: 40px)
 * @returns {number|string} Calculated scroll width in pixels for desktop, or '100%' for mobile/tablet
 */
export const calculateTableScrollX = (sidebarCollapsed, minWidth = 600, maxWidth = 1000, buffer = 40) => {
  if (typeof window === 'undefined') {
    return maxWidth; // Fallback to maxWidth
  }

  const width = window.innerWidth;
  
  // Mobile and Tablet: use full available width (tables handle this via CSS)
  if (width < 1024) {
    return '100%';
  }

  // Desktop: Use the exact column width (minWidth and maxWidth should be the same = column sum)
  // This ensures tables fit exactly to their column widths on desktop
  if (minWidth === maxWidth) {
    return maxWidth; // Return exact column width
  }

  const availableWidth = calculateAvailableWidth(sidebarCollapsed, buffer);
  
  // If availableWidth is a string (100%), return it
  if (typeof availableWidth === 'string') {
    return availableWidth;
  }
  
  // Clamp between minWidth and maxWidth (for cases where min != max)
  return Math.min(Math.max(availableWidth, minWidth), maxWidth);
};

/**
 * Calculate header width based on sidebar state
 * @param {boolean} sidebarCollapsed - Whether the sidebar is collapsed
 * @returns {number} Header width in pixels
 */
export const calculateHeaderWidth = (sidebarCollapsed) => {
  if (typeof window === 'undefined') {
    return 0;
  }

  const sidebarWidth = sidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;
  return window.innerWidth - sidebarWidth;
};

/**
 * Calculate responsive modal width based on screen size
 * @param {string|number} desktopWidth - Width for desktop (≥1024px). Can be percentage string (e.g., "35%") or number in pixels
 * @returns {string} Responsive width string
 */
export const calculateModalWidth = (desktopWidth = '35%') => {
  if (typeof window === 'undefined') {
    return desktopWidth;
  }

  const width = window.innerWidth;
  
  if (width < 768) {
    // Mobile: 95% of viewport
    return '95%';
  } else if (width < 1024) {
    // Tablet: 80% of viewport
    return '80%';
  } else {
    // Desktop: Use provided width
    return desktopWidth;
  }
};

// Export constants for use in other modules
export const WIDTH_CONSTANTS = {
  SIDEBAR_WIDTH_EXPANDED,
  SIDEBAR_WIDTH_COLLAPSED,
  CONTENT_MARGIN,
  CONTENT_PADDING,
  TOTAL_FIXED_SPACE,
};

